import { type ChangeEvent, useRef, useState } from 'react';
import { useT } from '../i18n';
import { compressPhotoToDataUrl } from '../lib/image';
import { Button } from './ui/Button';

interface PhotoCaptureProps {
  value: string | undefined;
  onChange: (dataUrl: string | undefined) => void;
}

function CameraIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M4 8h3l1.5-2h7L17 8h3a1 1 0 0 1 1 1v10a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V9a1 1 0 0 1 1-1Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <circle cx="12" cy="14" r="3.2" stroke="currentColor" strokeWidth="1.6" />
    </svg>
  );
}

/** Captura de foto do comprovante — abre a câmera do celular direto (RF-VIA-04). */
export function PhotoCapture({ value, onChange }: PhotoCaptureProps) {
  const { t } = useT();
  const inputRef = useRef<HTMLInputElement>(null);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string>();

  async function handleFile(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setProcessing(true);
    setError(undefined);
    try {
      const dataUrl = await compressPhotoToDataUrl(file);
      onChange(dataUrl);
    } catch {
      setError(t('expenses.photo.error'));
    } finally {
      setProcessing(false);
    }
  }

  return (
    <div className="flex flex-col gap-2">
      <span className="text-sm font-medium text-ink-muted">{t('expenses.photo.label')}</span>
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={(event) => void handleFile(event)}
      />

      {value ? (
        <div className="flex items-center gap-3">
          <img
            src={value}
            alt={t('expenses.photo.alt')}
            className="h-20 w-20 rounded-lg border border-line object-cover"
          />
          <div className="flex flex-col gap-2">
            <Button
              variant="secondary"
              onClick={() => inputRef.current?.click()}
              className="min-h-0 px-3 py-2 text-sm"
            >
              {t('expenses.photo.change')}
            </Button>
            <Button
              variant="ghost"
              onClick={() => onChange(undefined)}
              className="min-h-0 px-3 py-2 text-sm"
            >
              {t('expenses.photo.remove')}
            </Button>
          </div>
        </div>
      ) : (
        <Button
          variant="secondary"
          fullWidth
          loading={processing}
          icon={<CameraIcon />}
          onClick={() => inputRef.current?.click()}
        >
          {t('expenses.photo.take')}
        </Button>
      )}

      {error ? <p className="text-sm text-danger">{error}</p> : null}
    </div>
  );
}
